import React, { Fragment, useEffect, useState } from 'react';
import './SubscriptionCancellationSheet.css';
import AnimatedPopup from '../../../../../components/AnimatedPopup/AnimatedPopup';
import * as translator from '../../../../../utils/translator';
import * as http from '../../../../../utils/http';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';
import { getCancellationFlowSource } from '../../../../../utils/cancellationFlowSource';
import successBadge from '../../../../../icons/careplus-success-badge-v2.png';
import successCancelOverlay from '../../../../../icons/careplus-success-cancel-overlay-v2.png';
import resultsCurve from '../../../../../icons/cf-results-curve-area.svg';
import resultsEndDot from '../../../../../icons/cf-results-end-dot.svg';
import resultsDotPassed from '../../../../../icons/cf-results-dot-w1.svg';
import resultsDotAhead from '../../../../../icons/cf-results-dot-w5.svg';
import resultsDoctor from '../../../../../icons/cf-results-doctor.png';
import resultsCheck from '../../../../../icons/cf-results-check.svg';
import resultsClockIcon from '../../../../../icons/cf-results-clock.svg';
import resultsSmileIcon from '../../../../../icons/cf-results-smile.svg';
import resultsGraphIcon from '../../../../../icons/cf-results-graph.svg';
import resultsFlagIcon from '../../../../../icons/cf-results-flag.svg';
import resultsStepCurrent from '../../../../../icons/cf-results-step-current.svg';
import resultsStepCurrentEnd from '../../../../../icons/cf-results-step-current-end.svg';
import heartHandshakeIcon from '../../../../../icons/cf-heart-handshake.svg';
import circleXIcon from '../../../../../icons/cf-circle-x.svg';
import dosageIcon from '../../../../../icons/cf-menu-dosage.svg';
import menuDoctorIcon from '../../../../../icons/cf-menu-doctor.svg';
import backIcon from '../../../../../icons/cf-back.svg';
import menuPauseIcon from '../../../../../icons/cf-menu-pause.svg';
import menuCapsuleIcon from '../../../../../icons/cf-menu-capsule.svg';
import menuCancelIcon from '../../../../../icons/cf-menu-cancel.svg';
import shieldOuter from '../../../../../icons/cf-done-shield-a.svg';
import shieldInner from '../../../../../icons/cf-done-shield-b.svg';
import doneCheck from '../../../../../icons/cf-done-check.svg';
import doneInfo from '../../../../../icons/cf-done-info.svg';
import doctorAvatar from '../../../../../icons/cf-doctor-avatar.png';
import chevronDown from '../../../../../icons/cf-chevron-down.svg';
import pauseBarsIcon from '../../../../../icons/cf-pause-bars.svg';
import infoIcon from '../../../../../icons/cf-info.svg';
import goalBadge from '../../../../../icons/cf-goal-badge.png';
import goalCard from '../../../../../icons/cf-goal-card.png';
import goalCardBg from '../../../../../icons/cf-goal-card-bg.svg';
import travelArrowhead from '../../../../../icons/cf-results-arrowhead.svg';
import arrivalCircle from '../../../../../icons/cf-results-arrival-circle.svg';
import DatePickerWrapper from '../../../../../components/DatePickerWrapper/DatePickerWrapper';

/**
 * WL DE cancellation flow. Opened from the existing "Cancel subscription" action on the Care+
 * subscription page. "Cancel" and "one-time order" create a cancellation request that the scheduler
 * executes around the next payment; pause reuses the existing Care+ pause modal.
 *
 * HL-12358: the server resolves a persona from the subscription tenure (A = days 0–10, B = day 11+).
 * Persona B gets one more reason ("I've reached my weight goal", its own screen), its own "not seeing
 * results" copy, and the 50% offer only for "too expensive"; Persona A ends every cancellation on the offer.
 */

export type Persona = 'A' | 'B';

type Step =
  | 'menu' | 'reasons' | 'offer' | 'offerDone' | 'results' | 'sideEffects' | 'tips'
  | 'doctor' | 'weightGoal' | 'other' | 'oneTime' | 'oneTimeDone' | 'requested' | 'pause' | 'pauseDone';

type Reason = 'too_expensive' | 'no_results' | 'side_effects' | 'doctor_dissatisfied' | 'weight_goal' | 'other';
/** Decisions without a server action of their own, reported to HubSpot as "Subscription Retained". */
type RetainedDecision = 'talk_to_doctor' | 'dosage_change' | 'keep_plan' | 'exit';
const REASONS: Record<Persona, Reason[]> = {
  A: ['too_expensive', 'no_results', 'side_effects', 'doctor_dissatisfied', 'other'],
  B: ['too_expensive', 'no_results', 'side_effects', 'doctor_dissatisfied', 'weight_goal', 'other'],
};
const REASON_STEP: Record<Reason, Step | null> = {
  too_expensive: null, // straight to the offer (or the request when no offer)
  no_results: 'results',
  side_effects: 'sideEffects',
  doctor_dissatisfied: 'doctor',
  weight_goal: 'weightGoal',
  other: 'other',
};
/** Persona B is offered the discount only for this reason; every other reason cancels straight through. */
const PERSONA_B_OFFER_REASONS: Reason[] = ['too_expensive'];
/** Free text of the "other" reason; the API refuses longer comments (SubscriptionCancellationRequestsManager.COMMENT_MAX_LENGTH). */
const COMMENT_MAX_LENGTH = 256;

/**
 * The curve of the results graph in the graph frame's px (the top edge of the Figma "Vector 20" area, from
 * the card's left edge to its right edge). The sheet draws the line on it: solid up to the customer's
 * position, dashed ahead of it. The animated "progress flow" (Figma 8445:19305 and its glow and bloom) rides
 * the same path. One 2.9 s loop: the line draws itself from the card's edge up to the customer's position,
 * an arrow rides its leading edge and the arrival ring appears where it stops (Figma "Progress timeline
 * mobile" 8181:2585: every week variant highlights the curve only up to "I'm here").
 */
type Point = { x: number; y: number };
/** The curve as cubic Bézier segments [start, control 1, control 2, end]; FLOW_PATH is the same curve as SVG path data. */
const FLOW_SEGMENTS: [Point, Point, Point, Point][] = [
  [{ x: -0.72, y: 24 }, { x: 12.63, y: 36.59 }, { x: 46.6, y: 66.6 }, { x: 100.5, y: 70 }],
  [{ x: 100.5, y: 70 }, { x: 195.5, y: 76 }, { x: 184.58, y: 67.12 }, { x: 244, y: 109 }],
  [{ x: 244, y: 109 }, { x: 293, y: 143.5 }, { x: 337.05, y: 127.3 }, { x: 351.2, y: 126.7 }],
];
const FLOW_PATH = `M${FLOW_SEGMENTS[0][0].x} ${FLOW_SEGMENTS[0][0].y}` + FLOW_SEGMENTS.map(([, c1, c2, end]) => `C${c1.x} ${c1.y} ${c2.x} ${c2.y} ${end.x} ${end.y}`).join('');
const FLOW_START_X = FLOW_SEGMENTS[0][0].x;
const FLOW_END_X = FLOW_SEGMENTS[FLOW_SEGMENTS.length - 1][3].x;

/** The curve sampled once (points at equal parameter steps, with the length run so far), to find a point at a fraction of its length. */
const FLOW_SAMPLES: (Point & { length: number })[] = (() => {
  const samples: (Point & { length: number })[] = [];
  let length = 0;
  FLOW_SEGMENTS.forEach(([p0, p1, p2, p3], s) => {
    for (let k = s === 0 ? 0 : 1; k <= 200; k += 1) {
      const u = k / 200; const v = 1 - u;
      const point = {
        x: v * v * v * p0.x + 3 * v * v * u * p1.x + 3 * v * u * u * p2.x + u * u * u * p3.x,
        y: v * v * v * p0.y + 3 * v * v * u * p1.y + 3 * v * u * u * p2.y + u * u * u * p3.y,
      };
      const prev = samples[samples.length - 1];
      if (prev) length += Math.hypot(point.x - prev.x, point.y - prev.y);
      samples.push({ ...point, length });
    }
  });
  return samples;
})();
/** The point on the curve at a fraction (0–1) of its length: where the drawn line stops and the arrow arrives. */
const pointAtFraction = (t: number): Point => {
  const target = Math.min(1, Math.max(0, t)) * FLOW_SAMPLES[FLOW_SAMPLES.length - 1].length;
  const i = Math.max(1, FLOW_SAMPLES.findIndex((s) => s.length >= target));
  const [a, b] = [FLOW_SAMPLES[i - 1], FLOW_SAMPLES[i]];
  const f = b.length === a.length ? 0 : (target - a.length) / (b.length - a.length);
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
};
/** The leading glow covers this much of the path (pathLength 1) once fully grown. */
const FLOW_GLOW_LENGTH = 0.2;

/**
 * "Not seeing results" progress graph, laid out on the Figma frame (343 × 204 px, its top 3 px clipped).
 * Milestones sit on the curve; "you are here" moves along it with the customer's treatment week.
 */
const MILESTONES = [
  // t = how far along FLOW_PATH (fraction of its length) the milestone sits; the traveler and the drawn line stop there.
  // A milestone the customer has passed shows the filled dot (Figma Ellipse 526), one ahead the hollow dot (Ellipse 525).
  { week: 1, key: 'w1', x: 20.5, y: 43, t: 0.0727, icon: resultsClockIcon },
  { week: 3, key: 'w3', x: 102.5, y: 69.5, t: 0.3052, icon: resultsSmileIcon },
  { week: 5, key: 'w5', x: 201.5, y: 84.5, t: 0.57, icon: resultsGraphIcon },
  { week: 9, key: 'w9', x: 298.4, y: 129.4, t: 0.8597, icon: resultsFlagIcon },
];
const milestoneDot = (m: (typeof MILESTONES)[number], week: number) =>
  m.week === 9 ? resultsEndDot : week >= m.week ? resultsDotPassed : resultsDotAhead;

/**
 * State of a timeline step (Figma "Timeline step mobile" 9094:61906), by comparing the milestone's week with
 * the customer's week: before it "completed" (check), the same week "current" (dot), after it "upcoming"
 * (outline circle with the milestone's own icon). Between two milestones nothing is current. "9+" is
 * open-ended, so it is current for every week from 9 on.
 */
type StepState = 'completed' | 'current' | 'upcoming';
const stepState = (m: (typeof MILESTONES)[number], week: number): StepState => {
  if (m.week === 9) return week >= 9 ? 'current' : 'upcoming';
  return week > m.week ? 'completed' : week === m.week ? 'current' : 'upcoming';
};

/**
 * Point on the curve for a treatment week: on a milestone its dot, between two milestones the point on the
 * curve at the interpolated fraction of its length (so it sits on the line, not on a chord between the dots).
 */
const curvePoint = (week: number) => {
  const first = MILESTONES[0];
  const last = MILESTONES[MILESTONES.length - 1];
  if (week <= first.week) return first;
  if (week >= last.week) return last;
  const i = MILESTONES.findIndex((m) => m.week > week);
  const [a, b] = [MILESTONES[i - 1], MILESTONES[i]];
  const t = a.t + (b.t - a.t) * ((week - a.week) / (b.week - a.week));
  return { ...pointAtFraction(t), t };
};

/** "[[text]]" in copy is rendered in italics (the doctor badge). */
const emphasised = (text: string) => (text || '').split(/(\[\[.*?\]\])/).map((part, i) =>
  part.startsWith('[[') ? <em key={i}>{part.slice(2, -2)}</em> : <Fragment key={i}>{part}</Fragment>);

/** "**text**" in copy is rendered bold (dates in the pause screens). */
const bolded = (text: string) => (text || '').split(/(\*\*.*?\*\*)/).map((part, i) =>
  part.startsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <Fragment key={i}>{part}</Fragment>);

/** 'YYYY-MM-DD' as a UTC date, so formatting never shifts the day. */
const isoDate = (date: string) => new Date(`${date}T00:00:00Z`);
const addOneMonth = (date: string) => {
  const d = isoDate(date);
  d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString().slice(0, 10);
};
const formatDate = (date: string, language: string, options: Intl.DateTimeFormatOptions) =>
  isoDate(date).toLocaleDateString(language, { ...options, timeZone: 'UTC' });
const todayISO = () => new Date().toISOString().slice(0, 10);
/** English is served to the UK only: day before month ("3 November 2026"), not the US "November 3, 2026". */
const dateLocale = (language: string) => (language === 'en' ? 'en-GB' : language);

interface Props {
  subscriptionID: number;
  country: string;
  language: string;
  offerAvailable: boolean;
  /** Resolved by the server from the subscription tenure; picks the screen variants and the reasons. */
  persona?: Persona;
  /** Next billing date ('YYYY-MM-DD'), shown on the offer confirmation. */
  nextChargeDate?: string | null;
  /** Treatment week (1-based) for the results graph. */
  treatmentWeek?: number;
  /** Dates the subscription may be paused until (inclusive); without it Pause opens the old pause modal. */
  pauseWindow?: { minDate: string; maxDate: string } | null;
  /** The subscription is paused: the pause and one-time order options are shown but disabled, and the cancellation confirmation says the plan stays paused. */
  isPaused?: boolean;
  /** Open straight on a step (the page's Pause button opens 'pause'). */
  initialStep?: Step;
  onClose: () => void;
  /** Flow finished with a state change (request created, offer accepted): refresh the page. */
  onChanged: () => void;
  onOpenPause: () => void;
  setStickyNotificationDetails: (details: any) => void;
}

const MIDDLEWARES = [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER];

const SubscriptionCancellationSheet: React.FC<Props> = ({
  subscriptionID, country, language, offerAvailable, persona = 'A', nextChargeDate = null, treatmentWeek = 1, pauseWindow = null, isPaused = false, initialStep = 'menu',
  onClose, onChanged, onOpenPause, setStickyNotificationDetails,
}) => {
  const [step, setStep] = useState<Step>(initialStep);
  const [pauseDate, setPauseDate] = useState<string>('');
  const [history, setHistory] = useState<Step[]>([]);
  const [reason, setReason] = useState<Reason | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [openTip, setOpenTip] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [changed, setChanged] = useState(false);
  /** cancel_at of the request just created: the subscription end date on the one-time confirmation. */
  const [requestCancelAt, setRequestCancelAt] = useState<string | null>(null);

  const t = (path: string) => translator.translate(language, 'my_account', `CancellationFlow/${path}`);

  /** Every event of the flow carries the persona, so A and B can be compared on each step. */
  const track = (type: 'pv' | 'custom', name: string, data: Record<string, unknown>) =>
    MainTracker.track(type, name, { ...data, persona });

  useEffect(() => {
    track('pv', 'View Pop Up', { 'Pop Up Name': `Subscription Cancellation Flow - ${step}` });
  }, [step]);

  /** "[[accent]]" in copy is rendered with the orange gradient. */
  const titled = (text: string) => (text || '').split(/(\[\[.*?\]\])/).map((part, i) =>
    part.startsWith('[[')
      ? <span key={i} className="scf__accent">{part.slice(2, -2)}</span>
      : <Fragment key={i}>{part}</Fragment>);

  const go = (next: Step) => { setHistory((h) => [...h, step]); setStep(next); };
  const back = () => setHistory((h) => { const prev = h[h.length - 1]; if (prev) setStep(prev); return h.slice(0, -1); });
  const close = () => (changed ? onChanged() : onClose());

  const showError = () => setStickyNotificationDetails({ show: true, level: 'error', title: t('errors/title'), content: t('errors/body') });

  /** Sent next to the matching Mixpanel decision. Tracking only: a failure never blocks the flow. `exitStep`: the screen X was pressed on. */
  const reportRetained = (decision: RetainedDecision, exitStep?: Step) =>
    http.postToServer('account-update/subscription/care-plus/cancellation-flow/retained', { subscriptionID, decision, ...(exitStep && { step: exitStep }) }, MIDDLEWARES, { timeout: 2000 })
      .catch(() => undefined);

  const createRequest = (body: Record<string, unknown>, doneStep: Step) => {
    setLoading(true);
    // HL-12631: a request made from the BO support link carries its source
    const source = getCancellationFlowSource();
    http.postToServer('account-update/subscription/care-plus/cancellation-flow/request', { subscriptionID, ...body, ...(source && { source }) }, MIDDLEWARES)
      .then((res: any) => {
        // a one-time order has no picked reason; the events ground truth gives it reason_id 'one_time_order', like the request row
        const isOneTimeOrder = body.type === 'one_time_order';
        track('custom', 'Decision on Cancel Subscription', { Decision: isOneTimeOrder ? 'one_time_order' : 'confirmed', reason_id: isOneTimeOrder ? 'one_time_order' : body.reason });
        setRequestCancelAt(res?.data?.request?.cancelAt ?? null);
        setChanged(true);
        setHistory([]);
        setStep(doneStep);
      })
      .catch(() => showError())
      .finally(() => setLoading(false));
  };

  /**
   * Persona A: every way to the cancellation ends on the 50% offer (when one is configured), as a last
   * resort. Persona B sees it only for "too expensive". The request is only created when the customer
   * declines it. Comment and rating come from state, so they survive the detour through the offer screen.
   */
  const requestCancellation = (offerDeclined: boolean) =>
    createRequest({ type: 'cancel', reason, offerDeclined, comment: comment.trim() || undefined, rating: rating ?? undefined }, 'requested');

  const offerApplies = offerAvailable && (persona === 'A' || (reason != null && PERSONA_B_OFFER_REASONS.includes(reason)));

  const continueCancel = () => {
    if (offerApplies) {
      go('offer');
      return;
    }
    requestCancellation(false);
  };

  const declineOffer = () => {
    track('custom', 'Decision on Retention Discount Offer', { Decision: 'declined', reason_id: reason });
    requestCancellation(true);
  };

  const submitReason = () => {
    if (!reason) return;
    track('custom', 'Select Cancellation Reason', { reason_id: reason });
    const next = REASON_STEP[reason];
    if (next) go(next); else continueCancel();
  };

  const acceptOffer = () => {
    setLoading(true);
    http.postToServer('account-update/subscription/care-plus/cancellation-flow/accept-offer', { subscriptionID, reason }, MIDDLEWARES)
      .then(() => {
        track('custom', 'Decision on Retention Discount Offer', { Decision: 'accepted', reason_id: reason });
        setChanged(true);
        setHistory([]);
        setStep('offerDone');
      })
      .catch(() => showError())
      .finally(() => setLoading(false));
  };

  const openPause = () => (pauseWindow ? go('pause') : onOpenPause());

  const pauseUntil = () => {
    if (!pauseDate) return;
    setLoading(true);
    // The page's own Pause button opens the sheet on 'pause': only a pause from the menu is a retention.
    http.postToServer('account-update/subscription/care-plus/pause', { subscriptionID, resumeDate: pauseDate, fromCancellationMenu: initialStep === 'menu' }, MIDDLEWARES)
      .then(() => {
        track('custom', 'Decision on Cancellation Menu', { Decision: 'pause_until', resume_date: pauseDate });
        setChanged(true);
        setHistory([]);
        setStep('pauseDone');
      })
      .catch(() => showError())
      .finally(() => setLoading(false));
  };

  /** Dosage change, dose review and the maintenance plan have no flow of their own: they open a message to the doctor. */
  const askDoctorPath = `/${country}/account/ask-a-doctor`;
  /**
   * Every CTA of a recovery screen that keeps the plan active (keep plan, talk to the doctor, chat with
   * support, …) is one `keep_plan` decision on that screen; a CTA that also navigates fires nothing else.
   */
  const trackKeepPlan = () => track('custom', 'Decision on Cancel Subscription', { Decision: 'keep_plan', step });

  /** Help links on the reason screens leave the flow: report the decision first, then follow the link. */
  const reportRetainedThenOpen = (decision: RetainedDecision, path: string) => (event: React.MouseEvent) => {
    // A new-tab click keeps this page open, so the report can finish on its own.
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      reportRetained(decision);
      return;
    }
    event.preventDefault();
    reportRetained(decision).then(() => { window.location.href = path; });
  };

  /** A recovery screen's link CTA: tracked as keep_plan, reported as retained, then the page opens. */
  const keepPlanThenOpen = (decision: RetainedDecision, path: string) => (event: React.MouseEvent) => {
    trackKeepPlan();
    reportRetainedThenOpen(decision, path)(event);
  };
  const askDoctorThenOpen = (decision: RetainedDecision) => keepPlanThenOpen(decision, askDoctorPath);

  const keepPlan = () => {
    trackKeepPlan();
    reportRetained('keep_plan');
    close();
  };

  /** The header's back arrow and close X, with the screen they leave. */
  const goBack = () => { track('custom', 'Decision on Cancel Subscription', { Decision: 'back', step }); back(); };
  const exit = () => {
    track('custom', 'Decision on Cancel Subscription', { Decision: 'exit', step });
    // Leaving the flow is a retention (the server skips the end screens). The page's own Pause button opens
    // the sheet on 'pause', which is not the cancellation flow.
    if (initialStep === 'menu') reportRetained('exit', step);
    close();
  };

  const longDate = (date: string) => formatDate(date, language, { day: 'numeric', month: 'long', year: 'numeric' });

  const radio = (checked: boolean) => <span className={`scf__radio ${checked ? 'scf__radio--on' : ''}`} aria-hidden="true" />;

  // Back sits in the popup's header row, left of its close X: same row, same size, never scrolled away.
  const backButton = (
    <button type="button" className="scf__back-btn" aria-label="Back" onClick={goBack}>
      <img src={backIcon} alt="" />
    </button>
  );

  /** Success badges from Figma: shield (with "%" for the discount), plus a green check (or "i") bubble. */
  const doneBadge = (kind: 'discount' | 'shield', bubble = doneCheck) => (
    <div className="scf__badge-v2" aria-hidden="true">
      <div className="scf__badge-circle">
        <div className="scf__badge-shield">
          <img src={shieldOuter} alt="" className="scf__shield-outer" />
          <img src={shieldInner} alt="" className="scf__shield-inner" />
        </div>
        {kind === 'discount' && <span className="scf__badge-percent">%</span>}
      </div>
      <span className="scf__badge-check"><img src={bubble} alt="" /></span>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'menu': {
        const option = (key: string, icon: string, iconSize: number, onClick: () => void, terminal = false, disabled = false) => (
          <button type="button" className={`scf__option ${terminal ? 'scf__option--terminal' : ''} ${disabled ? 'scf__option--disabled' : ''}`} onClick={onClick} disabled={disabled} aria-disabled={disabled}>
            <span className="scf__option-icon"><img src={icon} alt="" width={iconSize} height={iconSize} /></span>
            <span className="scf__option-text"><span className="scf__option-title">{t(`menu/${key}Title`)}</span><span className="scf__option-desc">{t(`menu/${key}Desc`)}</span></span>
            <span className="scf__option-radio" aria-hidden="true" />
          </button>
        );
        return (
          <>
            <h2 className="scf__title">{titled(t('menu/title'))}</h2>
            <p className="scf__body">{t('menu/body')}</p>
            <div className="scf__options">
              {option('doctor', menuDoctorIcon, 21, () => { track('custom', 'Decision on Cancellation Menu', { Decision: 'talk_to_doctor' }); reportRetained('talk_to_doctor').then(() => { window.location.href = askDoctorPath; }); }, true)}
              {option('dosage', dosageIcon, 22, () => { track('custom', 'Decision on Cancellation Menu', { Decision: 'dosage_change' }); reportRetained('dosage_change').then(() => { window.location.href = askDoctorPath; }); })}
              {/* paused: both shown, neither clickable (their dates would come from a charge date the pause made stale) */}
              {option('pause', menuPauseIcon, 12.4, () => { track('custom', 'Decision on Cancellation Menu', { Decision: 'pause' }); openPause(); }, false, isPaused)}
              {option('oneTime', menuCapsuleIcon, 20.2, () => { track('custom', 'Decision on Cancellation Menu', { Decision: 'one_time' }); go('oneTime'); }, false, isPaused)}
              {option('cancel', menuCancelIcon, 22.9, () => { track('custom', 'Decision on Cancellation Menu', { Decision: 'cancel' }); go('reasons'); }, true)}
            </div>
          </>
        );
      }

      case 'reasons':
        return (
          <>
            <h2 className="scf__title scf__title--semi">{titled(t('reasons/title'))}</h2>
            <p className="scf__body">{t('reasons/body')}</p>
            <div className="scf__radios" role="radiogroup">
              {REASONS[persona].map((r) => (
                <button key={r} type="button" role="radio" aria-checked={reason === r} className="scf__radio-row" onClick={() => setReason(r)}>
                  {radio(reason === r)}<span>{t(`reasons/${r}`)}</span>
                </button>
              ))}
            </div>
            <div className="scf__footer">
              <button type="button" className="scf__btn-primary" onClick={keepPlan}>{t('reasons/keep')}</button>
              <button type="button" className={reason ? 'scf__btn-outline-red' : 'scf__btn-disabled'} disabled={!reason || loading} onClick={submitReason}>
                {t('reasons/continue')}
              </button>
            </div>
          </>
        );

      case 'offer':
        return (
          <>
            <h2 className="scf__title">{titled(t('offer/title'))}</h2>
            <div className="scf__offer">
              <span className="scf__offer-badge">{t('offer/badge')}</span>
              <div className="scf__offer-big">{t('offer/big')}</div>
              <div className="scf__offer-sub">{t('offer/sub')}</div>
              <p className="scf__offer-body">{t('offer/body')}</p>
              <div className="scf__chips">
                <span className="scf__chip">{t('offer/chipTreatment')}</span>
                <span className="scf__chip">{t('offer/chipService')}</span>
                <span className="scf__chip">{t('offer/chipShipping')}</span>
              </div>
            </div>
            <div className="scf__footer">
              <button type="button" className="scf__btn-primary" disabled={loading} onClick={acceptOffer}>{t('offer/accept')}</button>
              <button type="button" className="scf__link" disabled={loading} onClick={declineOffer}>
                {t('offer/decline')}
              </button>
            </div>
          </>
        );

      case 'results': {
        const here = curvePoint(treatmentWeek);
        // Persona B: same graph and timeline, its own title and copy, and a dose review with the doctor as the CTA
        const copy = persona === 'B' ? 'resultsB' : 'results';
        // how far along the flow line (fraction of its length) the line draws and the arrow travels: up to the customer
        const travelEnd = Math.min(1, Math.max(0, here.t));
        // the label sits centred under the point, except low on the curve where it would run into the doctor badge
        // (left 22–235 px, top 124 px): there it moves right of the badge, as in Figma's week 8 frame (centre 286 px)
        const hereLabelX = here.y + 35 > 124 ? Math.max(here.x, 286) : here.x;
        const flowVars = {
          '--scf-end': travelEnd,
          '--scf-glow-len': Math.min(FLOW_GLOW_LENGTH, travelEnd),
          '--scf-glow-off': -Math.max(0, travelEnd - FLOW_GLOW_LENGTH),
        } as React.CSSProperties;
        return (
          <>
            <h2 className="scf__title">{titled(t(`${copy}/title`))}</h2>
            <p className="scf__body">{t(`${copy}/subtitle`)}</p>
            <div className="scf__graph-wrap">
              <div className="scf__graph" role="img" aria-label={`${t('results/youAreHere')}: ${t('results/weekShort')} ${treatmentWeek}`}>
                <div className="scf__graph-frame">
                  <div className="scf__graph-curve"><img src={resultsCurve} alt="" /></div>
                  {/* the drawn line, its bloom and its leading glow (Figma 8445:19305 / 18925 / 18936), one 2.9 s loop, drawn up to the customer */}
                  <svg className="scf__graph-flow" viewBox="0 0 343 204" width="343" height="204" aria-hidden="true" style={flowVars}>
                    <defs>
                      <linearGradient id="scf-flow-gradient" gradientUnits="userSpaceOnUse" x1={FLOW_START_X} x2={FLOW_END_X} y1="57.28" y2="57.28">
                        <stop stopColor="#FFBF1A" /><stop offset="0.55" stopColor="#FF8708" /><stop offset="1" stopColor="#E85700" />
                      </linearGradient>
                    </defs>
                    <g fill="none" strokeLinecap="round">
                      {/* the part of the curve still ahead of the customer: a dashed line (the solid line below covers it up to the customer) */}
                      <path className="scf__flow-track" d={FLOW_PATH} stroke="url(#scf-flow-gradient)" strokeWidth="1.5" strokeDasharray="4 3" />
                      <path className="scf__flow-bloom" d={FLOW_PATH} pathLength={1} stroke="url(#scf-flow-gradient)" strokeWidth="10" />
                      <path className="scf__flow-glow" d={FLOW_PATH} pathLength={1} stroke="#FF7A08" strokeWidth="7" />
                      <path className="scf__flow-line" d={FLOW_PATH} pathLength={1} stroke="url(#scf-flow-gradient)" strokeWidth="2.5" />
                    </g>
                  </svg>
                  <div className="scf__graph-guide" />
                  {MILESTONES.map((m) => (
                    <Fragment key={m.key}>
                      <img src={milestoneDot(m, treatmentWeek)} alt="" className={`scf__graph-dot scf__graph-dot--${m.key}`} />
                      <span className={`scf__graph-week scf__graph-week--${m.key}`}>{t('results/weekShort')} {m.week}{m.week === 9 ? '+' : ''}</span>
                    </Fragment>
                  ))}
                  {/* the customer's position on the curve (Figma "Current position"): a filled dot under the arriving arrow */}
                  <img src={resultsDotPassed} alt="" className="scf__graph-dot scf__graph-dot--here" style={{ left: `${here.x - 4.5}px`, top: `${here.y - 4.5}px` }} />
                  <div
                    className="scf__graph-traveler"
                    style={{ offsetPath: `path("${FLOW_PATH}")`, '--scf-travel-end': `${(travelEnd * 100).toFixed(2)}%` } as React.CSSProperties}
                    aria-hidden="true"
                  >
                    <img src={arrivalCircle} alt="" className="scf__traveler-arrival" />
                    <img src={travelArrowhead} alt="" className="scf__traveler-arrow" />
                  </div>
                  <div className="scf__graph-average">
                    <span className="scf__graph-average-value">{t('results/averageValue')}</span>
                    <span className="scf__graph-average-label">{t('results/average')}</span>
                  </div>
                  <span className="scf__graph-here" style={{ left: `${Math.min(Math.max(hereLabelX, 44), 299)}px`, top: `${here.y + 10}px` }}>
                    {t('results/youAreHere')}
                  </span>
                </div>
              </div>
              <div className="scf__doctor-badge">
                <span className="scf__doctor-photo"><img src={resultsDoctor} alt="" /></span>
                <span className="scf__doctor-text">{emphasised(t('results/doctorNote'))}</span>
              </div>
            </div>
            <ol className="scf__timeline">
              {MILESTONES.map((m) => {
                const state = stepState(m, treatmentWeek);
                const icon = state === 'completed' ? resultsCheck : state === 'current' ? (m.week === 9 ? resultsStepCurrentEnd : resultsStepCurrent) : m.icon;
                return (
                  <li key={m.key} className={`scf__timeline-item scf__timeline-item--${state === 'completed' ? 'done' : state}`}>
                    <span className="scf__timeline-dot" aria-hidden="true"><img src={icon} alt="" /></span>
                    <span><span className="scf__timeline-title">{t(`results/${m.key}Title`)}</span><span className="scf__timeline-text">{t(`results/${m.key}Text`)}</span></span>
                  </li>
                );
              })}
            </ol>
            <div className="scf__footer">
              {persona === 'B'
                ? <a className="scf__btn-primary" href={askDoctorPath} onClick={askDoctorThenOpen('dosage_change')}>{t('resultsB/doctor')}</a>
                : <button type="button" className="scf__btn-primary" onClick={keepPlan}>{t('results/continue')}</button>}
              <button type="button" className="scf__link" onClick={() => continueCancel()}>{t(`${copy}/cancelAnyway`)}</button>
            </div>
          </>
        );
      }

      case 'weightGoal':
        return (
          <div className="scf__goal">
            <div className="scf__badge-v2 scf__badge-v2--goal" aria-hidden="true">
              <img src={goalBadge} alt="" className="scf__goal-badge" />
              <span className="scf__badge-check"><img src={doneCheck} alt="" /></span>
            </div>
            <h2 className="scf__title scf__title--semi scf__title--center">{titled(t('weightGoal/title'))}</h2>
            <p className="scf__body scf__body--center scf__body--sm">{t('weightGoal/body')}</p>
            <div className="scf__goal-card" aria-hidden="true">
              <img src={goalCardBg} alt="" className="scf__goal-card-bg" />
              <img src={goalCard} alt="" className="scf__goal-card-art" />
              <span className="scf__goal-card-pill">{t('weightGoal/cardBadge')}</span>
              <p className="scf__goal-card-title">{titled(t('weightGoal/cardTitle'))}</p>
            </div>
            <div className="scf__footer">
              <a className="scf__btn-primary" href={askDoctorPath} onClick={askDoctorThenOpen('talk_to_doctor')}>{t('weightGoal/doctor')}</a>
              <button type="button" className="scf__link" onClick={() => continueCancel()}>{t('weightGoal/cancelAnyway')}</button>
            </div>
          </div>
        );

      case 'sideEffects':
        return (
          <>
            <div className="scf__hero-card">
              <div className="scf__eyebrow"><img src={heartHandshakeIcon} alt="" />{t('sideEffects/eyebrow')}</div>
              <h2 className="scf__title">{titled(t('sideEffects/title'))}</h2>
              <p className="scf__body">{t('sideEffects/body')}</p>
            </div>
            <p className="scf__label">{t('sideEffects/commonLabel')}</p>
            <div className="scf__chips">
              {[1, 2, 3, 4, 5].map((i) => <span key={i} className="scf__tag">{t(`sideEffects/chip${i}`)}</span>)}
            </div>
            <div className="scf__info">
              <p className="scf__info-title">{t('sideEffects/infoTitle')}</p>
              <p className="scf__info-body">{t('sideEffects/infoBody')}</p>
            </div>
            {/* Dosage change is only explained here; the way to it is talking to the doctor (button below). */}
            <div className="scf__actions-box">
              <p className="scf__info-title">{t('sideEffects/actionsTitle')}</p>
              <p className="scf__actions-item"><strong>{t('sideEffects/dosageLabel')}</strong> {t('sideEffects/dosageText')}</p>
            </div>
            <div className="scf__footer">
              <a className="scf__btn-primary" href={askDoctorPath} onClick={askDoctorThenOpen('talk_to_doctor')}>{t('sideEffects/talk')}</a>
              <button type="button" className="scf__btn-outline-orange" onClick={() => go('tips')}>{t('sideEffects/tips')}</button>
              <button type="button" className="scf__link scf__link--sm scf__link--icon" onClick={() => continueCancel()}>
                <img src={circleXIcon} alt="" />{t('sideEffects/continueCancel')}
              </button>
            </div>
          </>
        );

      case 'tips':
        return (
          <>
            <h2 className="scf__title scf__title--22">{titled(t('tips/title'))}</h2>
            <div className="scf__accordion">
              {[1, 2, 3].map((i, idx) => (
                <div key={i} className={`scf__acc-item ${openTip === idx ? 'scf__acc-item--open' : ''}`}>
                  <button type="button" className="scf__acc-head" aria-expanded={openTip === idx} onClick={() => setOpenTip(openTip === idx ? -1 : idx)}>
                    <span>{t(`tips/q${i}`)}</span><img className="scf__acc-chevron" src={chevronDown} alt="" aria-hidden="true" />
                  </button>
                  {openTip === idx && (
                    <div className="scf__acc-body">
                      <p>{t(`tips/a${i}p1`)}</p>
                      <p><span className="scf__solution">{t('tips/solutionLabel')}</span> {t(`tips/a${i}p2`)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="scf__footer">
              <a className="scf__btn-primary" href={askDoctorPath} onClick={askDoctorThenOpen('talk_to_doctor')}>{t('tips/message')}</a>
              <button type="button" className="scf__link" onClick={() => continueCancel()}>{t('tips/continueCancel')}</button>
            </div>
          </>
        );

      case 'doctor':
        return (
          <>
            <h2 className="scf__title scf__title--23">{titled(t('doctor/title'))}</h2>
            <div className="scf__offer">
              <div className="scf__doctor-row">
                <span className="scf__doctor-avatar"><img src={doctorAvatar} alt="" /></span>
                <span className="scf__doctor-heading">{t('doctor/cardTitle')}</span>
                <span className="scf__offer-badge">{t('doctor/free')}</span>
              </div>
              <p className="scf__offer-body">{t('doctor/body')}</p>
            </div>
            <div className="scf__footer">
              <a className="scf__btn-primary" href={`/${country}/contact`} onClick={keepPlanThenOpen('talk_to_doctor', `/${country}/contact`)}>{t('doctor/chat')}</a>
              <button type="button" className="scf__link" onClick={() => continueCancel()}>{t('doctor/cancelAnyway')}</button>
            </div>
          </>
        );

      case 'other':
        return (
          <>
            <h2 className="scf__title scf__title--semi">{titled(t('other/title'))}</h2>
            <p className="scf__body">{t('other/body')}</p>
            <p className="scf__label">{t('other/ratingLabel')}</p>
            <div className="scf__emojis" role="radiogroup">
              {['😞', '😕', '😐', '🙂', '😄'].map((emoji, i) => (
                <button key={emoji} type="button" role="radio" aria-checked={rating === i + 1} className={`scf__emoji ${rating === i + 1 ? 'scf__emoji--on' : ''}`} onClick={() => setRating(i + 1)}>
                  <span className="scf__emoji-circle">{emoji}</span>
                  <span className="scf__emoji-label">{t(`other/rating${i + 1}`)}</span>
                </button>
              ))}
            </div>
            <label className="scf__label" htmlFor="scf-feedback">{t('other/moreLabel')}</label>
            <textarea id="scf-feedback" className="scf__textarea" maxLength={COMMENT_MAX_LENGTH} value={comment} placeholder={t('other/placeholder')} onChange={(e) => setComment(e.target.value)} />
            <span className="scf__textarea-count" aria-live="polite">{`${comment.length}/${COMMENT_MAX_LENGTH}`}</span>
            <div className="scf__footer">
              <button type="button" className="scf__btn-outline-grey" disabled={loading} onClick={() => continueCancel()}>
                {t('other/submit')}
              </button>
            </div>
          </>
        );

      // HL-12966 (Figma 3:9538): laid out like the outcome screens, one screen for last_shipment current and next
      case 'oneTime':
        return (
          <div className="scf__done scf__done--one-time">
            {doneBadge('shield', doneInfo)}
            <h2 className="scf__done-title">{titled(t('oneTime/title'))}</h2>
            <p className="scf__one-time-body">{t('oneTime/body')}</p>
            <button type="button" className="scf__btn-primary" disabled={loading} onClick={() => createRequest({ type: 'one_time_order' }, 'oneTimeDone')}>
              {t('oneTime/submit')}
            </button>
          </div>
        );

      // HL-12966 (Figma 3:9982): the day the subscription ends is the request's cancel_at
      case 'oneTimeDone': {
        const endDate = requestCancelAt
          ? formatDate(String(requestCancelAt).slice(0, 10), dateLocale(language), { day: 'numeric', month: 'long', year: 'numeric' })
          : '';
        return (
          <div className="scf__done scf__done--one-time">
            {doneBadge('shield')}
            <h2 className="scf__done-title">{titled(t('oneTimeDone/title'))}</h2>
            <p className="scf__body scf__body--center">
              {endDate ? t('oneTimeDone/body').replace(':subscriptionEndDate', endDate) : t('oneTimeDone/bodyNoDate')}
            </p>
            <button type="button" className="scf__btn-ghost" onClick={close}>{t('oneTimeDone/done')}</button>
          </div>
        );
      }

      case 'pause': {
        const followDate = pauseDate ? addOneMonth(pauseDate) : '';
        const short = (d: string) => formatDate(d, language, { day: '2-digit', month: '2-digit', year: '2-digit' });
        return (
          <>
            <h2 className="scf__title scf__title--semi">{titled(t('pause/title'))}</h2>
            <p className="scf__body">{t('pause/body')}</p>
            <div className="scf__pause-card">
              <p className="scf__pause-card-title">{t('pause/rangeTitle')}</p>
              <div className="scf__pause-range">
                <label className="scf__pause-field">
                  <span>{t('pause/from')}</span>
                  <span className="scf__pause-input">{short(todayISO())}</span>
                </label>
                <label className="scf__pause-field">
                  <span>{t('pause/to')}</span>
                  <span className={`scf__pause-input scf__pause-input--active ${pauseDate ? '' : 'scf__pause-input--empty'}`}>
                    {pauseDate ? short(pauseDate) : t('pause/placeholder')}
                  </span>
                </label>
              </div>
              <div className="scf__pause-calendar">
                <DatePickerWrapper
                  onChange={(value: string) => setPauseDate(value || '')}
                  errorMode={false}
                  value={pauseDate}
                  isBirthdayInput={false}
                  inline
                  variant="appointment"
                  minDate={pauseWindow ? isoDate(pauseWindow.minDate) : null}
                  maxDate={pauseWindow ? isoDate(pauseWindow.maxDate) : null}
                  localizeWeekdays
                  openToMinDate
                  language={language}
                />
              </div>
              {pauseDate && (
                <div className="scf__pause-note">
                  <img src={infoIcon} alt="" />
                  <p>{bolded(t('pause/note')
                    .replace(':nextDate', formatDate(pauseDate, language, { day: 'numeric', month: 'long', year: 'numeric' }))
                    .replace(':followDate', formatDate(followDate, language, { day: 'numeric', month: 'long', year: 'numeric' })))}</p>
                </div>
              )}
            </div>
            <div className="scf__footer">
              <button type="button" className="scf__btn-outline-grey scf__btn-icon" disabled={loading || !pauseDate} onClick={pauseUntil}>
                <img src={pauseBarsIcon} alt="" />{t('pause/submit')}
              </button>
            </div>
          </>
        );
      }

      case 'pauseDone': {
        const followDate = addOneMonth(pauseDate);
        const rowDate = (d: string) => formatDate(d, language, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
        return (
          <div className="scf__done">
            {doneBadge('shield')}
            <h2 className="scf__done-title">{titled(t('pauseDone/title'))}</h2>
            <p className="scf__body scf__body--center">{bolded(t('pauseDone/body').replace(':date', formatDate(pauseDate, language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })))}</p>
            <dl className="scf__pause-rows">
              <div><dt>{t('pauseDone/nextLabel')}</dt><dd>{rowDate(pauseDate)}</dd></div>
              <div><dt>{t('pauseDone/followLabel')}</dt><dd>{rowDate(followDate)}</dd></div>
            </dl>
            <button type="button" className="scf__btn-ghost" onClick={close}>{t('pauseDone/done')}</button>
            <p className="scf__done-footnote">{t('pauseDone/footnote').replace(':date', formatDate(pauseDate, language, { day: 'numeric', month: 'long' }))}</p>
          </div>
        );
      }

      case 'offerDone':
      case 'requested': {
        const key = step;
        return (
          <div className="scf__done">
            {key === 'requested' ? (
              <div className="scf__badge-wrap">
                <img src={successBadge} alt="" className="scf__badge" />
                <img src={successCancelOverlay} alt="" className="scf__badge-overlay" />
              </div>
            ) : doneBadge('discount')}
            <h2 className="scf__done-title">{titled(t(`${key}/title`))}</h2>
            {key === 'requested' && <p className="scf__done-subtitle">{t('requested/subtitle')}</p>}
            <p className="scf__body scf__body--center">
              {t(key === 'requested' && isPaused ? 'requested/bodyPaused' : `${key}/body`)
                // the offer confirmation names the next billing date
                .replace(':nextChargeDate', nextChargeDate ? longDate(nextChargeDate) : '')}
            </p>
            {key === 'requested' && <p className="scf__done-note">{t('requested/note')}</p>}
            <button type="button" className={key === 'offerDone' ? 'scf__btn-primary' : 'scf__btn-ghost'} onClick={close}>
              {t(key === 'offerDone' ? 'offerDone/button' : `${key}/done`)}
            </button>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const isDone = step === 'requested' || step === 'offerDone' || step === 'oneTimeDone' || step === 'pauseDone';

  return (
    <AnimatedPopup
      isVisible
      title=""
      onClose={exit}
      scrollable
      fitContent
      containerClassName="scf__popup"
      contentClassName="scf__popup-content"
      headerLeftContent={!isDone && history.length > 0 ? backButton : undefined}
    >
      <div className="scf">
        {renderStep()}
      </div>
    </AnimatedPopup>
  );
};

export default SubscriptionCancellationSheet;
